import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Badge, Button,
  Form, Alert, Spinner, Modal, Table,
} from 'react-bootstrap';
import {
  getPartnerSummary, generatePartnerInvoice,
  sendPartnerInvoice, downloadPartnerInvoiceUrl,
} from './DataService';

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const getPreviousMonth = () => {
  const now = new Date();
  const m = now.getMonth(); // 0-11
  return { year: m === 0 ? now.getFullYear() - 1 : now.getFullYear(), month: m === 0 ? 12 : m };
};

const PartnerBillingScreen = () => {
  const prev = getPreviousMonth();
  const currentYear = new Date().getFullYear();

  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedYear, setSelectedYear] = useState(prev.year);
  const [selectedMonth, setSelectedMonth] = useState(prev.month);

  // Modal génération
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Modal envoi
  const [showSendModal, setShowSendModal] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, [selectedYear, selectedMonth]);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPartnerSummary(selectedYear, selectedMonth || null);
      setSummary(data.summary || []);
    } catch (err) {
      setError('Erreur lors de la récupération des données');
    } finally {
      setLoading(false);
    }
  };

  const totalAFacturer = summary.reduce((s, i) => s + i.total_ttc, 0);

  const handleGenerate = (item) => {
    setSelectedItem(item);
    setShowGenerateModal(true);
  };

  const confirmGenerate = async () => {
    if (!selectedItem) return;
    setGenerating(true);
    setError(null);
    try {
      const result = await generatePartnerInvoice(
        selectedItem.partner_token_id,
        selectedItem.year,
        selectedItem.month,
      );
      if (result.success) {
        setSuccess(`Facture ${result.num_facture} générée avec succès !`);
        setGeneratedInvoice({ ...result, partner_email: selectedItem.partner_email });
        setShowGenerateModal(false);
        setShowSendModal(true);
        fetchSummary();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!generatedInvoice) return;
    setSending(true);
    setError(null);
    try {
      const result = await sendPartnerInvoice(generatedInvoice.num_facture, customMessage || null);
      if (result.success) {
        setSuccess(`Facture envoyée à ${result.email}`);
        setShowSendModal(false);
        setCustomMessage('');
        setGeneratedInvoice(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Facturation Partenaires</h2>
          <p className="text-muted">Commandes API (source=3) — facturation mensuelle par partenaire</p>
        </Col>
      </Row>

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Année</Form.Label>
                <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
                  {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Mois</Form.Label>
                <Form.Select
                  value={selectedMonth || ''}
                  onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Tous les mois</option>
                  {MONTH_NAMES.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button variant="primary" onClick={fetchSummary} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Actualiser'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPI */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-primary h-100">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">À facturer</h6>
              <h3 className="text-primary mb-0">{totalAFacturer.toFixed(2)} €</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-secondary h-100">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Partenaires</h6>
              <h3 className="text-secondary mb-0">
                {new Set(summary.map((i) => i.partner_token_id)).size}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-info h-100">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Commandes</h6>
              <h3 className="text-info mb-0">{summary.reduce((s, i) => s + i.orders_count, 0)}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* Liste */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : summary.length === 0 ? (
        <Alert variant="info">Aucune commande partenaire à facturer pour cette période.</Alert>
      ) : (
        <Row>
          {summary.map((item, idx) => (
            <Col key={idx} md={6} lg={4} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-dark text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <strong>{item.partner_name}</strong>
                    <Badge bg="light" text="dark">Partenaire</Badge>
                  </div>
                  <small>{item.partner_email || <span className="text-warning">⚠️ Email non configuré</span>}</small>
                </Card.Header>
                <Card.Body>
                  <h6 className="text-muted mb-2">
                    {MONTH_NAMES[item.month - 1]} {item.year}
                  </h6>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span>{item.orders_count} commande{item.orders_count > 1 ? 's' : ''}</span>
                    <h4 className="text-primary mb-0">{item.total_ttc.toFixed(2)} €</h4>
                  </div>

                  {item.orders && item.orders.length > 0 && (
                    <div className="mb-3" style={{ maxHeight: 140, overflowY: 'auto' }}>
                      <Table size="sm" className="mb-0">
                        <tbody>
                          {item.orders.map((o) => (
                            <tr key={o.id}>
                              <td><small>#{o.id}</small></td>
                              <td><small>{o.DateCommande}</small></td>
                              <td className="text-end"><small>{o.TTC.toFixed(2)} €</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  <Button
                    variant="success"
                    className="w-100"
                    onClick={() => handleGenerate(item)}
                    disabled={!item.partner_email}
                    title={!item.partner_email ? 'Email partenaire non configuré' : ''}
                  >
                    Générer la facture
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Modal génération */}
      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Générer une facture partenaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <>
              <p>Vous allez générer une facture pour :</p>
              <ul>
                <li><strong>Partenaire :</strong> {selectedItem.partner_name}</li>
                <li><strong>Email :</strong> {selectedItem.partner_email}</li>
                <li><strong>Période :</strong> {MONTH_NAMES[selectedItem.month - 1]} {selectedItem.year}</li>
                <li><strong>Commandes :</strong> {selectedItem.orders_count}</li>
                <li><strong>Total TTC :</strong> {selectedItem.total_ttc.toFixed(2)} €</li>
              </ul>
              <Alert variant="warning" className="mb-0">
                ⚠️ Cette action marquera les commandes comme facturées.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)} disabled={generating}>
            Annuler
          </Button>
          <Button variant="primary" onClick={confirmGenerate} disabled={generating}>
            {generating ? <Spinner animation="border" size="sm" /> : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal envoi */}
      <Modal show={showSendModal} onHide={() => setShowSendModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Envoyer la facture par email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generatedInvoice && (
            <>
              <Alert variant="success">
                Facture <strong>{generatedInvoice.num_facture}</strong> générée ({generatedInvoice.orders_count} commandes — {parseFloat(generatedInvoice.total_ttc).toFixed(2)} €)
              </Alert>
              <p>Envoyer maintenant à <strong>{generatedInvoice.partner_email}</strong> ?</p>
              <Form.Group className="mb-3">
                <Form.Label>Message personnalisé (optionnel)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Ajoutez un message personnalisé..."
                  maxLength={1000}
                />
              </Form.Group>
              {generatedInvoice.pdf_url && (
                <a href={generatedInvoice.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                  Prévisualiser le PDF
                </a>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)} disabled={sending}>
            Plus tard
          </Button>
          <Button variant="primary" onClick={handleSend} disabled={sending}>
            {sending ? <Spinner animation="border" size="sm" /> : 'Envoyer maintenant'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PartnerBillingScreen;
